import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GET_OBS_TOP_DARES_CONFIG } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import setObsTopDaresConfig from 'root/src/client/logic/creatorData/thunks/setObsTopDaresConfig';
import { dareDropClient } from 'root/src/client/v2/common/clients/dareDropClient';
import { globalQueryClient } from 'root/src/client/v2/common/clients/queryClient';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
const DEFAULT_TEXT = 'Put your donations to good use';
const DEFAULT_COLOR = '#22d3ee';
export const OBSTopDaresSection = () => {
    const dispatch = useAppDispatch();
    const { authentication } = useAuthentication();
    const configQuery = useQuery({
        queryKey: [GET_OBS_TOP_DARES_CONFIG],
        queryFn: () => dareDropClient({ endpointId: GET_OBS_TOP_DARES_CONFIG }),
        enabled: !!authentication,
    }, globalQueryClient);
    const [flairColor, setFlairColor] = useState(DEFAULT_COLOR);
    const [overlayText, setOverlayText] = useState(DEFAULT_TEXT);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const dirtyRef = useRef(false);
    const saveTimer = useRef(null);
    // Seed from the saved config once it loads. Does not flag dirty.
    useEffect(() => {
        const data = configQuery.data;
        if (!data) {
            return;
        }
        setFlairColor(data.flairColor || DEFAULT_COLOR);
        setOverlayText(data.overlayText || DEFAULT_TEXT);
    }, [configQuery.data]);
    const save = () => {
        dirtyRef.current = false;
        setSaving(true);
        setFailed(false);
        const promise = Promise.resolve(dispatch(setObsTopDaresConfig({ flairColor, overlayText })));
        toast.promise(promise, {
            loading: 'Saving overlay...',
            success: 'Top dares overlay saved',
            error: 'Could not save overlay. Please try again.',
        });
        promise.then(() => {
            setSaving(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 1300);
            globalQueryClient.invalidateQueries({ queryKey: [GET_OBS_TOP_DARES_CONFIG] });
        }, () => {
            setSaving(false);
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
        });
    };
    // Debounced auto-save on user edits; the Save button also commits immediately.
    useEffect(() => {
        if (!dirtyRef.current) {
            return undefined;
        }
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        saveTimer.current = setTimeout(save, 800);
        return () => {
            if (saveTimer.current) {
                clearTimeout(saveTimer.current);
            }
        };
    }, [flairColor, overlayText]);
    const changeColor = (value) => {
        dirtyRef.current = true;
        setFlairColor(value);
    };
    const changeText = (value) => {
        dirtyRef.current = true;
        setOverlayText(value);
    };
    const handleSave = () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        save();
    };
    if (configQuery.isLoading) {
        return (<div className="space-y-4">
				<Skeleton className="h-5 w-40"/>
				<Skeleton className="h-9 w-full"/>
			</div>);
    }
    return (<div>
			<div className="mb-1 flex items-center justify-between gap-2">
				<h3 className="text-sm font-semibold">Top Dares overlay</h3>
				<Button variant="outline" size="sm" loading={saving} success={saved} error={failed} onClick={handleSave} className="h-8 min-w-[100px] rounded-lg text-[11px]">
					Save
				</Button>
			</div>
			<p className="mb-4 text-xs text-muted-foreground">Customize the donations overlay shown on your stream.</p>
			<div className="space-y-4">
				<Field label="Overlay text" htmlFor="overlay-text">
					<Input id="overlay-text" value={overlayText} onChange={event => changeText(event.target.value)} maxLength={120}/>
				</Field>
				<div>
					<span className="mb-1 block text-[11px] font-medium text-muted-foreground">Flair color</span>
					<div className="flex items-center gap-3">
						<input type="color" value={flairColor} onChange={event => changeColor(event.target.value)} aria-label="Flair color" className="h-9 w-14 cursor-pointer rounded-lg border border-solid border-border/50 bg-transparent p-1"/>
						<span className="text-[11px] uppercase tabular-nums text-muted-foreground">{flairColor}</span>
					</div>
				</div>
			</div>
		</div>);
};
